import type { ForecastField } from '../../api/forecastFieldsService';
import { getFieldIcon, getFieldIconComponent } from './icons';
import { MS_TO_KMH } from '../../utils/weatherUtils';

/**
 * Render types for different visualization styles in the forecast table
 */
export type FieldRenderType = 'value' | 'sparkline' | 'bar' | 'wind' | 'weatherCode' | 'direction';

// Re-export icon helpers for use in components
export { getFieldIcon, getFieldIconComponent };

/**
 * Configuration for how a field should be rendered in the forecast table
 */
export interface FieldRenderConfig {
  /** Emoji icon to display in the row header */
  icon: string;
  /** Type of visualization to use */
  renderType: FieldRenderType;
  /** Color for sparklines and bars */
  color?: string;
  /** Maximum value for bar charts (e.g., 100 for percentage) */
  maxValue?: number;
  /** Function to determine cell background color based on value */
  getColor?: (value: number | null) => string;
  /** db_name of another field to combine with (e.g., wind_direction shown with wind_speed) */
  combinedWith?: string;
  /** Format function for display value */
  formatValue?: (value: string | null, unit: string | null) => string;
}

/**
 * Map db_name to render configuration.
 * Fields not in this map use intelligent defaults based on their unit.
 */
export const FIELD_RENDER_CONFIG: Record<string, FieldRenderConfig> = {
  // Weather code - special icon rendering
  weather_code: { 
    icon: '☁️', 
    renderType: 'weatherCode' 
  },
  
  // Probability/percentage fields - bar visualization
  rain_probability: { 
    icon: '☔', 
    renderType: 'bar', 
    color: '#1890ff', 
    maxValue: 100 
  },
  precipitation_probability: { 
    icon: '☔', 
    renderType: 'bar', 
    color: '#52c41a', 
    maxValue: 100 
  },
  cloud_cover: { 
    icon: '☁️', 
    renderType: 'bar', 
    color: '#8c8c8c', 
    maxValue: 100 
  },
  
  // Temperature fields - sparkline visualization
  track_temp_max: { 
    icon: '🏁', 
    renderType: 'sparkline', 
    color: '#ff7a45' 
  },
  track_temp_min: { 
    icon: '🏁', 
    renderType: 'sparkline', 
    color: '#ff7a45' 
  },
  temperature: { 
    icon: '🌡️', 
    renderType: 'sparkline', 
    color: '#1890ff' 
  },
  temperature_apparent: { 
    icon: '🌡️', 
    renderType: 'sparkline', 
    color: '#1890ff' 
  },
  heat_index: { 
    icon: '🥵', 
    renderType: 'sparkline',
    color: '#f5222d',  // Red for heat index
  },
  dew_point: { 
    icon: '🌫️', 
    renderType: 'value' 
  },
  
  // Wind fields - special coloring + direction arrows
  // Note: Wind values are stored in m/s, converted to km/h for display and color thresholds
  wind_speed: { 
    icon: '🌬️', 
    renderType: 'wind',
    combinedWith: 'wind_direction',
    getColor: (speedKmh) => {
      // speedKmh is already converted from m/s in parseWindValue
      if (speedKmh === null) return 'transparent';
      if (speedKmh <= 15) return '#52c41a'; // Green - Low (≤15 km/h)
      if (speedKmh <= 25) return '#fa8c16'; // Orange - Medium (≤25 km/h)
      return '#f5222d'; // Red - High (>25 km/h)
    }
  },
  // Min/max wind speed for legacy daily forecasts (split from wind_speed range)
  min_wind_speed: { 
    icon: '🌬️', 
    renderType: 'wind',
    getColor: (speedKmh) => {
      // Same thresholds as wind_speed
      if (speedKmh === null) return 'transparent';
      if (speedKmh <= 15) return '#52c41a'; // Green - Low (≤15 km/h)
      if (speedKmh <= 25) return '#fa8c16'; // Orange - Medium (≤25 km/h)
      return '#f5222d'; // Red - High (>25 km/h)
    }
  },
  max_wind_speed: { 
    icon: '🌬️', 
    renderType: 'wind',
    getColor: (speedKmh) => {
      // Same thresholds as wind_speed
      if (speedKmh === null) return 'transparent';
      if (speedKmh <= 15) return '#52c41a'; // Green - Low (≤15 km/h)
      if (speedKmh <= 25) return '#fa8c16'; // Orange - Medium (≤25 km/h)
      return '#f5222d'; // Red - High (>25 km/h)
    }
  },
  avg_wind_speed: { 
    icon: '🌬️', 
    renderType: 'wind',
    getColor: (speedKmh) => {
      // Same thresholds as wind_speed/min_wind_speed/max_wind_speed
      if (speedKmh === null) return 'transparent';
      if (speedKmh <= 15) return '#52c41a'; // Green - Low (≤15 km/h)
      if (speedKmh <= 25) return '#fa8c16'; // Orange - Medium (≤25 km/h)
      return '#f5222d'; // Red - High (>25 km/h)
    }
  },
  wind_gust: { 
    icon: '💨', 
    renderType: 'wind',
    getColor: (speedKmh) => {
      if (speedKmh === null) return 'transparent';
      if (speedKmh <= 25) return '#fa8c16'; // Orange - Low-Medium (≤25 km/h)
      if (speedKmh <= 50) return '#f5222d'; // Red - High (≤50 km/h)
      return '#a8071a'; // Dark Red - Very High (>50 km/h)
    }
  },
  wind_gusts: { 
    icon: '💨', 
    renderType: 'wind',
    getColor: (speedKmh) => {
      // speedKmh is already converted from m/s in parseWindValue
      if (speedKmh === null) return 'transparent';
      if (speedKmh <= 25) return '#fa8c16'; // Orange - Low-Medium (≤25 km/h)
      if (speedKmh <= 50) return '#f5222d'; // Red - High (≤50 km/h)
      return '#a8071a'; // Dark Red - Very High (>50 km/h)
    }
  },
  wind_direction: {
    icon: '🧭',
    renderType: 'direction',  // Special render type - shows arrow instead of value
  },
  
  // Standard value fields
  humidity: { icon: '💦', renderType: 'bar', color: '#1890ff', maxValue: 100 },
  surface_pressure: { icon: '🎚️', renderType: 'value' },
  pressure_surface_level: { icon: '🎚️', renderType: 'value' },
  sea_pressure: { icon: '🎚️', renderType: 'value' },
  pressure_sea_level: { icon: '🎚️', renderType: 'value' },
  visibility: { icon: '👁️', renderType: 'value' },
  uv_index: { icon: '☀️', renderType: 'value' },
  solar_ghi: { icon: '☀️', renderType: 'value' },
  precipitation_intensity: { icon: '🌧️', renderType: 'value' },
  
  // Daily-specific temperature fields - each with own sparkline row
  max_temp: { 
    icon: '🌡️', 
    renderType: 'sparkline',
    color: '#ff7a45',  // Orange for max temp
  },
  min_temp: { 
    icon: '🌡️', 
    renderType: 'sparkline',
    color: '#1890ff',  // Blue for min temp
  },
};

/**
 * Fields that should be hidden because they're combined into another field's display
 * Note: wind_direction was previously here but is now rendered as its own field with arrow
 */
export const COMBINED_FIELDS = new Set<string>([
  // Currently empty - all fields render independently
]);

/**
 * Fields that are handled specially in daily view (shown in card title, not parameters list)
 * These are always shown but not as dynamic parameters
 */
export const DAILY_SPECIAL_FIELDS = new Set([
  'weather_code_am',
  'weather_code_pm',
]);

/**
 * Fields that should be combined/grouped in daily view
 * Key is the "main" field, value is the field to combine with it
 * Note: max_temp/min_temp are now rendered as separate rows
 */
export const DAILY_COMBINED_FIELDS: Record<string, string> = {
  // Currently empty - all fields render as separate rows
};

/**
 * Get the render configuration for a field.
 * Uses explicit config if available, otherwise returns smart defaults based on field properties.
 */
export function getFieldRenderConfig(field: ForecastField): FieldRenderConfig {
  const config = FIELD_RENDER_CONFIG[field.db_name];
  if (config) return config;
  
  // Smart defaults based on field properties
  if (field.unit === '%') {
    return { icon: '📊', renderType: 'bar', color: '#1890ff', maxValue: 100 };
  }
  if (field.unit === '°C' || field.unit === '°F' || field.unit === 'C' || field.unit === 'F') {
    return { icon: '🌡️', renderType: 'sparkline', color: '#1890ff' };
  }
  if (field.db_name.includes('temp')) {
    return { icon: '🌡️', renderType: 'sparkline', color: '#1890ff' };
  }
  if (field.db_name.includes('wind') || field.db_name.includes('gust')) {
    return { icon: '🌬️', renderType: 'value' };
  }
  if (field.db_name.includes('rain') || field.db_name.includes('precip')) {
    return { icon: '🌧️', renderType: 'value' };
  }
  if (field.db_name.includes('humidity')) {
    return { icon: '💧', renderType: 'value' };
  }
  if (field.db_name.includes('pressure')) {
    return { icon: '🎚️', renderType: 'value' };
  }
  
  // Default for unknown fields
  return { icon: '📊', renderType: 'value' };
}

/**
 * Check if a field should be hidden because it's combined into another field's row
 */
export function isFieldCombined(dbName: string): boolean {
  return COMBINED_FIELDS.has(dbName);
}

/**
 * Parse wind value and convert from m/s to km/h for display
 * Wind data is stored in m/s (matching Tomorrow.io API and station observations)
 * but displayed in km/h for user convenience
 */
export function parseWindValue(value: string | null): number | null {
  if (!value) return null;
  const valueMs = parseFloat(value);
  if (isNaN(valueMs)) return null;
  // Convert from m/s to km/h for display
  return valueMs * MS_TO_KMH;
}

/**
 * Parse percentage value (now always a numeric value after data alignment)
 */
export function parsePercentageValue(value: string | null): number | null {
  if (!value) return null;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Format a value with its unit for display
 */
export function formatValueWithUnit(value: string | null, unit: string | null): string {
  if (value === null || value === undefined) return 'N/A';
  
  // If unit is already in the value, don't add it again
  if (unit && !value.includes(unit)) {
    return `${value} ${unit}`;
  }
  return value;
}

